import { LitElement, html, PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { ButtonSettings, ButtonStrings } from '../types';

export class GzCollapsableContainerWebComponent extends LitElement {
  @property({ type: Number, attribute: true, reflect: true })
  fixedContentHeight: number;

  //this needs to be changed alongside fixedContentHeight
  @property({ type: Number, attribute: true, reflect: true })
  numberOfLines: number;

  @property({ type: Object, attribute: true, reflect: true })
  buttonStrings: ButtonStrings;

  @property({ type: Object, attribute: true, reflect: true })
  buttonSettings: ButtonSettings = {
    customClass: '',
    type: 'quiet',
    color: 'primary',
    buttonHeight: 'small',
    buttonFontSize: 'small',
    theme: 'light',
    lowercase: false,
  };

  /**
   * Determines if the control button (Show more/Show less) is shown
   * @private isContentOverflow
   */
  @state()
  private isContentOverflow = false;

  /**
   * Determines which text is placed on the control button
   * @private shouldCollapse
   */
  @state()
  private shouldCollapse = false;

  @query('slot')
  private slotElement: HTMLSlotElement;

  private resizeObserver: ResizeObserver;

  static get styles() {
    return styles;
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(() => this.handleContentOverflow());

    this.resizeObserver.observe(this);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    this.resizeObserver.unobserve(this);
  }

  updated(properties: PropertyValues): void {
    if (properties.has('fixedContentHeight')) {
      this.adjustSlotDimensions();
    }
  }

  render() {
    return html`
      <div class="container">
        <div class="slot-container">
          <slot @slotchange="${this.handleSlotChange}"></slot>
        </div>

        <div part="button-show-more" class=${this.isContentOverflow ? 'button-container' : 'hide-button-container'}>
          <gz-button
            class="${this.buttonSettings.customClass}"
            type="${this.buttonSettings.type}"
            color="${this.buttonSettings.color}"
            buttonHeight="${this.buttonSettings.buttonHeight}"
            buttonFontSize="${this.buttonSettings.buttonFontSize}"
            theme="${this.buttonSettings.theme}"
            ?lowercase="${this.buttonSettings.lowercase}"
            @click="${this.handleButtonClick}"
            >${this.shouldCollapse ? this.buttonStrings.showMore : this.buttonStrings.showLess}</gz-button
          >
        </div>
      </div>
    `;
  }

  private handleButtonClick(event: Event): void {
    const element = event.target as HTMLElement;
    this.shouldCollapse = !this.shouldCollapse;
    this.adjustSlotDimensions();
    element.blur();
  }

  private handleContentOverflow(): void {
    const contentHeight = this.getTotalSlotHeight();
    this.isContentOverflow = contentHeight > this.fixedContentHeight;
  }

  private handleSlotChange(): void {
    const contentHeight = this.getTotalSlotHeight();

    if (this.fixedContentHeight && contentHeight > this.fixedContentHeight) {
      this.shouldCollapse = true;
      this.isContentOverflow = true;
    }
    this.adjustSlotDimensions();
  }

  private adjustSlotDimensions(): void {
    const dynamicHeight = this.fixedContentHeight && this.shouldCollapse ? `${this.fixedContentHeight}px` : '100%';
    this.style.setProperty('--content-dynamic-height', dynamicHeight);

    if (this.fixedContentHeight && this.shouldCollapse) {
      this.slotElement.classList.add('clamped');
      if (this.numberOfLines) {
        this.style.setProperty('--content-clamp-number', this.numberOfLines.toString());
      }
    } else {
      this.slotElement.classList.remove('clamped');
      this.handleContentOverflow();
    }
  }

  private getTotalSlotHeight(): number {
    let contentHeight = 0;
    this.slotElement.assignedElements().forEach(element => (contentHeight += element.scrollHeight));
    return contentHeight;
  }
}
